import { DEFAULT_PLAYER_NAME, SAVE_KEY, SCENE_KEYS } from '../data/gameConstants';
import { QUESTS } from '../data/quests';
import { SCORE_VALUES } from '../data/scoreRules';
import { getZoneSpawn } from '../data/zoneSpawns';

const VALID_SCENES = new Set<string>(Object.values(SCENE_KEYS));

export interface SaveData {
  playerName: string;
  rafflesiaPoints: number;
  currentZone: number;
  currentScene: string;
  playerX?: number;
  playerY?: number;
  completedQuests: string[];
  collectedKeris: string[];
  questProgress: Record<string, number>;
  collectedItems: string[];
  earthquakeNoHit?: boolean;
  gameCompleted?: boolean;
  missionBonusAwarded?: boolean;
  /** Skor saat pertama masuk zona berjalan — dipakai Ulangi Zona. */
  scoreAtZoneStart?: number;
  /** Poin tas siaga terakhir, untuk rollback skor Zona 3. */
  emergencyBagPoints?: number;
  timestamp: number;
}

export interface SaveSummary {
  playerName: string;
  currentZone: number;
  rafflesiaPoints: number;
  timestamp: number;
  currentScene: string;
}

export interface SavePosition {
  x: number;
  y: number;
}

const DEFAULT_SAVE: SaveData = {
  playerName: '',
  rafflesiaPoints: 0,
  currentZone: 1,
  currentScene: SCENE_KEYS.ZONE1,
  completedQuests: [],
  collectedKeris: [],
  questProgress: {},
  collectedItems: [],
  earthquakeNoHit: false,
  gameCompleted: false,
  missionBonusAwarded: false,
  emergencyBagPoints: 0,
  timestamp: 0,
};

export class SaveSystem {
  /** Lewati autosave shutdown saat ulangi zona — cegah timpa spawn & quest reset. */
  private static zoneRestartPending = false;

  static isZoneRestartPending(): boolean {
    return SaveSystem.zoneRestartPending;
  }

  static clearZoneRestartPending(): void {
    SaveSystem.zoneRestartPending = false;
  }

  private static getStorage(): Storage | null {
    try {
      if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
        return null;
      }
      return globalThis.localStorage;
    } catch {
      return null;
    }
  }

  private static readRaw(): string | null {
    try {
      return SaveSystem.getStorage()?.getItem(SAVE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private static writeRaw(value: string): boolean {
    try {
      const storage = SaveSystem.getStorage();
      if (!storage) return false;
      storage.setItem(SAVE_KEY, value);
      return true;
    } catch {
      return false;
    }
  }

  static isStorageAvailable(): boolean {
    try {
      const storage = SaveSystem.getStorage();
      if (!storage) return false;
      const probeKey = `${SAVE_KEY}__probe`;
      storage.setItem(probeKey, '1');
      storage.removeItem(probeKey);
      return true;
    } catch {
      return false;
    }
  }

  static hasSave(): boolean {
    try {
      const raw = SaveSystem.readRaw();
      if (!raw) return false;
      const data = JSON.parse(raw) as Partial<SaveData>;
      return typeof data.timestamp === 'number' && data.timestamp > 0;
    } catch {
      return false;
    }
  }

  static getSummary(): SaveSummary | null {
    if (!SaveSystem.hasSave()) return null;
    const save = SaveSystem.load();
    return {
      playerName: save.playerName.trim() || DEFAULT_PLAYER_NAME,
      currentZone: save.currentZone,
      rafflesiaPoints: save.rafflesiaPoints,
      timestamp: save.timestamp,
      currentScene: save.currentScene,
    };
  }

  static load(): SaveData {
    try {
      const raw = SaveSystem.readRaw();
      if (!raw) return { ...DEFAULT_SAVE };
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      return SaveSystem.sanitize({
        ...DEFAULT_SAVE,
        ...parsed,
        collectedKeris: Array.isArray(parsed.collectedKeris) ? parsed.collectedKeris : [],
        completedQuests: Array.isArray(parsed.completedQuests) ? parsed.completedQuests : [],
        questProgress: parsed.questProgress && typeof parsed.questProgress === 'object'
          ? parsed.questProgress
          : {},
        collectedItems: Array.isArray(parsed.collectedItems) ? parsed.collectedItems : [],
      });
    } catch {
      return { ...DEFAULT_SAVE };
    }
  }

  private static sanitize(data: SaveData): SaveData {
    let points = data.rafflesiaPoints;
    if (!Number.isFinite(points) || Number.isNaN(points)) points = 0;
    points = Math.max(0, Math.round(points));

    let zone = data.currentZone;
    if (!Number.isFinite(zone) || zone < 1 || zone > 5) zone = 1;

    let scene = data.currentScene;
    if (!VALID_SCENES.has(scene)) {
      scene = SCENE_KEYS.ZONE1;
      zone = 1;
    }

    const name = typeof data.playerName === 'string' ? data.playerName : '';
    const playerX = Number.isFinite(data.playerX) ? data.playerX : undefined;
    const playerY = Number.isFinite(data.playerY) ? data.playerY : undefined;

    let scoreAtZoneStart = data.scoreAtZoneStart;
    if (!Number.isFinite(scoreAtZoneStart) || Number.isNaN(scoreAtZoneStart)) {
      scoreAtZoneStart = undefined;
    } else {
      scoreAtZoneStart = Math.max(0, Math.round(scoreAtZoneStart as number));
    }

    let emergencyBagPoints = data.emergencyBagPoints;
    if (!Number.isFinite(emergencyBagPoints) || Number.isNaN(emergencyBagPoints)) {
      emergencyBagPoints = 0;
    } else {
      emergencyBagPoints = Math.max(0, Math.round(emergencyBagPoints as number));
    }

    return {
      ...data,
      playerName: name,
      rafflesiaPoints: points,
      currentZone: zone,
      currentScene: scene,
      playerX,
      playerY,
      earthquakeNoHit: data.earthquakeNoHit === true,
      gameCompleted: data.gameCompleted === true,
      missionBonusAwarded: data.missionBonusAwarded === true,
      scoreAtZoneStart,
      emergencyBagPoints,
    };
  }

  static save(data: Partial<SaveData>): void {
    SaveSystem.commitSave(data);
  }

  private static commitSave(data: Partial<SaveData>): void {
    const current = SaveSystem.load();
    const merged: SaveData = SaveSystem.sanitize({
      ...current,
      ...data,
      timestamp: Date.now(),
    });
    SaveSystem.writeRaw(JSON.stringify(merged));
  }

  static clear(): void {
    try {
      SaveSystem.getStorage()?.removeItem(SAVE_KEY);
    } catch {
      // ignore storage removal failures
    }
  }

  static getPlayerName(): string {
    const name = SaveSystem.load().playerName.trim();
    return name.length > 0 ? name : DEFAULT_PLAYER_NAME;
  }

  static createNew(playerName: string): SaveData {
    const resolved = playerName.trim() || DEFAULT_PLAYER_NAME;
    const data: SaveData = {
      ...DEFAULT_SAVE,
      playerName: resolved,
      currentZone: 1,
      currentScene: SCENE_KEYS.ZONE1,
      timestamp: Date.now(),
    };
    SaveSystem.writeRaw(JSON.stringify(data));
    return data;
  }

  static saveProgress(scene: string, zone: number, position?: SavePosition): void {
    if (SaveSystem.zoneRestartPending) return;
    const save = SaveSystem.load();
    const patch: Partial<SaveData> = {
      currentScene: scene,
      currentZone: zone,
      ...(position ? { playerX: Math.round(position.x), playerY: Math.round(position.y) } : {}),
    };
    if (!SaveSystem.zoneHasProgress(save, zone)) {
      patch.scoreAtZoneStart = save.rafflesiaPoints;
    }
    SaveSystem.save(patch);
  }

  /** Simpan progres hanya jika pemain masih di scene ini (hindari timpa save zona tujuan saat transisi). */
  static saveShutdownIfCurrentScene(scene: string, zone: number, position?: SavePosition): void {
    if (SaveSystem.zoneRestartPending) return;
    const save = SaveSystem.load();
    if (save.currentScene !== scene) return;
    SaveSystem.saveProgress(scene, zone, position);
  }

  static collectKeris(kerisId: string): void {
    const save = SaveSystem.load();
    if (!save.collectedKeris.includes(kerisId)) {
      SaveSystem.save({ collectedKeris: [...save.collectedKeris, kerisId] });
    }
  }

  /** Scene untuk tombol Lanjutkan — setelah tamat kembali ke zona terakhir yang bisa dimainkan */
  static getResumeScene(): string {
    const save = SaveSystem.load();
    if (save.currentScene === SCENE_KEYS.ENDING) {
      return SCENE_KEYS.ZONE5;
    }
    if (save.currentScene === SCENE_KEYS.NAME_INPUT
      || save.currentScene === SCENE_KEYS.PRELOAD
      || save.currentScene === SCENE_KEYS.BOOT) {
      return SCENE_KEYS.ZONE1;
    }
    return save.currentScene || SCENE_KEYS.ZONE1;
  }

  static getContinueScene(): string {
    return SaveSystem.getResumeScene();
  }

  /** Nomor zona dari scene key — konsisten untuk resume & transisi. */
  static getZoneForScene(scene: string): number {
    switch (scene) {
      case SCENE_KEYS.ZONE2: return 2;
      case SCENE_KEYS.ZONE3: return 3;
      case SCENE_KEYS.ZONE4: return 4;
      case SCENE_KEYS.ZONE5:
      case SCENE_KEYS.ENDING:
        return 5;
      default:
        return 1;
    }
  }

  /** Tentukan posisi awal sebelum saveProgress mengubah currentScene. */
  static getPlayerStartPosition(scene: string, spawnX: number, spawnY: number): SavePosition {
    const save = SaveSystem.load();
    if (SaveSystem.zoneRestartPending && save.currentScene === scene) {
      const spawn = getZoneSpawn(scene);
      if (spawn) return spawn;
    }
    if (save.currentScene !== scene) {
      return { x: spawnX, y: spawnY };
    }
    if (!Number.isFinite(save.playerX) || !Number.isFinite(save.playerY)) {
      return { x: spawnX, y: spawnY };
    }
    return {
      x: save.playerX!,
      y: save.playerY!,
    };
  }

  static getResumePosition(scene: string, fallbackX: number, fallbackY: number): SavePosition {
    return SaveSystem.getPlayerStartPosition(scene, fallbackX, fallbackY);
  }

  static markGameCompleted(): void {
    SaveSystem.save({ gameCompleted: true, currentScene: SCENE_KEYS.ENDING, currentZone: 5 });
  }

  static isItemCollected(itemId: string): boolean {
    return SaveSystem.load().collectedItems.includes(itemId);
  }

  static markItemCollected(itemId: string): void {
    const save = SaveSystem.load();
    if (!save.collectedItems.includes(itemId)) {
      SaveSystem.save({ collectedItems: [...save.collectedItems, itemId] });
    }
  }

  /** Reset progres zona saat ini lalu siapkan restart dari spawn. */
  static restartZone(zone: number, sceneKey: string): void {
    SaveSystem.zoneRestartPending = false;
    const save = SaveSystem.load();
    const zoneQuestIds = new Set(
      QUESTS.filter((q) => q.zone === zone).map((q) => q.id),
    );
    const zonePrefix = `zone${zone}_`;

    const completedQuests = save.completedQuests.filter((id) => !zoneQuestIds.has(id));
    const questProgress = { ...save.questProgress };
    zoneQuestIds.forEach((id) => {
      delete questProgress[id];
    });

    const collectedItems = save.collectedItems.filter((id) => !id.startsWith(zonePrefix));
    let collectedKeris = [...save.collectedKeris];
    const kerisForZone: Record<number, string> = { 2: 'keris_1', 3: 'keris_2', 4: 'keris_3' };
    const kerisId = kerisForZone[zone];
    if (kerisId) {
      collectedKeris = collectedKeris.filter((k) => k !== kerisId);
    }

    const spawn = getZoneSpawn(sceneKey);
    const restoredScore = SaveSystem.scoreBeforeZone(save, zone);

    SaveSystem.commitSave({
      currentScene: sceneKey,
      currentZone: zone,
      completedQuests,
      questProgress,
      collectedItems,
      collectedKeris,
      rafflesiaPoints: restoredScore,
      scoreAtZoneStart: restoredScore,
      emergencyBagPoints: zone === 3 ? 0 : save.emergencyBagPoints,
      earthquakeNoHit: zone === 2 ? false : save.earthquakeNoHit,
      gameCompleted: false,
      missionBonusAwarded: zone === 5 ? false : save.missionBonusAwarded,
      ...(spawn ? { playerX: spawn.x, playerY: spawn.y } : {}),
    });
    SaveSystem.zoneRestartPending = true;
  }

  private static zoneHasProgress(save: SaveData, zone: number): boolean {
    const kerisForZone: Record<number, string> = { 2: 'keris_1', 3: 'keris_2', 4: 'keris_3' };
    if (kerisForZone[zone] && save.collectedKeris.includes(kerisForZone[zone])) return true;
    if (save.collectedItems.some((id) => id.startsWith(`zone${zone}_`))) return true;
    return save.completedQuests.some((id) => QUESTS.find((q) => q.id === id)?.zone === zone);
  }

  private static scoreBeforeZone(save: SaveData, zone: number): number {
    if (Number.isFinite(save.scoreAtZoneStart)) {
      return Math.max(0, Math.round(save.scoreAtZoneStart as number));
    }
    return Math.max(0, save.rafflesiaPoints - SaveSystem.estimateZonePoints(save, zone));
  }

  private static estimateZonePoints(save: SaveData, zone: number): number {
    const done = new Set(save.completedQuests);
    let points = 0;
    if (zone === 1 && (done.has('zone1_collect_rafflesia') || save.collectedItems.includes('zone1_rafflesia_hint'))) {
      points += SCORE_VALUES.RAFFLESIA_HINT;
    }
    if (zone === 2) {
      if (save.collectedKeris.includes('keris_1') || save.collectedItems.includes('zone2_keris1')) {
        points += SCORE_VALUES.KERIS;
      }
      if (save.earthquakeNoHit) points += SCORE_VALUES.HAZARD_DODGE_BONUS;
    }
    if (zone === 3) {
      if (save.collectedKeris.includes('keris_2') || save.collectedItems.includes('zone3_keris2')) {
        points += SCORE_VALUES.KERIS;
      }
      if (done.has('zone3_emergency_bag')) {
        points += save.emergencyBagPoints ?? 0;
      }
    }
    if (zone === 4) {
      if (save.collectedKeris.includes('keris_3') || save.collectedItems.includes('zone4_keris3')) {
        points += SCORE_VALUES.KERIS;
      }
      if (done.has('zone4_safe_route')) points += SCORE_VALUES.SAFE_ROUTE;
    }
    if (zone === 5) {
      if (done.has('zone5_escort_safe')) points += SCORE_VALUES.HELP_CITIZENS;
      if (save.missionBonusAwarded) points += SCORE_VALUES.MISSION_COMPLETE;
    }
    return points;
  }
}
